import { ReactNode } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import {
  CounterInputV2,
  DropdownV2,
  FormCardV2,
  FormGridV2,
  MultiDropdownV2,
  OptionV2,
} from "../FormControlsV2";
import { ReactComponent as CalendarIcon } from "../../assets/svg/icons/calendar.svg";
import { normalizeMaxRate, normalizeRatingValues } from "./pastureManagementRating";
import { useTranslation } from "react-i18next";
import { es, ptBR } from "date-fns/locale";

export type PastureManagementFormFieldV2 =
  | "farm"
  | "area"
  | "microAreas"
  | "type"
  | "monitor"
  | "heightInput"
  | "heightOutput"
  | "occupationDays"
  | "restDays"
  | "maxRate"
  | "rateIn"
  | "rateOut"
  | "range"
  | "executionPerDay"
  | "frequency"
  | "startDate";

export type PastureManagementFormValueV2 = {
  farm: OptionV2 | null;
  area: OptionV2 | null;
  microAreas: OptionV2[];
  type: OptionV2 | null;
  monitor: OptionV2 | null;
  heightInput: number;
  heightOutput: number;
  occupationDays: number;
  restDays: number;
  maxRate: number;
  rateIn: number;
  rateOut: number;
  range: number;
  executionPerDay: number;
  frequency: OptionV2 | null;
};

export type PastureManagementFormErrorsV2 = Partial<
  Record<PastureManagementFormFieldV2, string>
>;

type PastureManagementFormV2Props = {
  value: PastureManagementFormValueV2;
  errors?: PastureManagementFormErrorsV2;
  farms: OptionV2[];
  areas: OptionV2[];
  microAreas: OptionV2[];
  managementTypes: OptionV2[];
  monitorTypes: OptionV2[];
  frequencyOptions: OptionV2[];
  selectedDays: boolean[];
  weekDays: string[];
  disabledDays: number[];
  startDate?: Date;
  endDate?: Date;
  disabled?: boolean;
  historyMode?: boolean;
  mapSlot?: ReactNode;
  onChange: (value: PastureManagementFormValueV2) => void;
  onFarmChange?: (option: OptionV2 | null) => void;
  onAreaChange?: (option: OptionV2 | null) => void;
  onMicroAreasChange?: (options: OptionV2[]) => void;
  onManagementTypeChange?: (option: OptionV2 | null) => void;
  onMonitorTypeChange?: (option: OptionV2 | null) => void;
  onFrequencyChange?: (option: OptionV2 | null) => void;
  onDayToggle?: (index: number) => void;
  onStartDateChange?: (date: Date) => void;
  onSubmit?: () => void;
};

function getOptionDomainName(option?: OptionV2 | null) {
  return (option as any)?.domainName || option?.label || "";
}

function isType(value: PastureManagementFormValueV2, label: string) {
  return getOptionDomainName(value.type) === label;
}

function isMonitor(value: PastureManagementFormValueV2, label: string) {
  return getOptionDomainName(value.monitor) === label;
}

function needsSchedule(value: PastureManagementFormValueV2) {
  return !!value.type && getOptionDomainName(value.type) !== "Sem monitoramento";
}

const ratingRangeOptions: OptionV2[] = [
  { label: "1 a 5", value: 5 },
  { label: "1 a 10", value: 10 },
];

function getScheduleLabel(value: PastureManagementFormValueV2, t: (key: string) => string) {
  if (!needsSchedule(value)) return "";
  if (!isType(value, "Em faixa") && value.frequency?.value === 1) {
    return t("formsV2.selectDaysAndFirstDate");
  }
  if (!isType(value, "Em faixa") && value.frequency?.value === 2) {
    return t("formsV2.selectDayAndFirstDate");
  }
  if (
    isType(value, "Em faixa") ||
    value.frequency?.value === 3 ||
    isMonitor(value, "Dias")
  ) {
    return t("formsV2.selectFirstDate");
  }
  return "";
}

export function PastureManagementFormV2({
  value,
  errors = {},
  farms,
  areas,
  microAreas,
  managementTypes,
  monitorTypes,
  frequencyOptions,
  selectedDays,
  weekDays,
  disabledDays,
  startDate,
  endDate,
  disabled = false,
  historyMode = false,
  mapSlot,
  onChange,
  onFarmChange,
  onAreaChange,
  onMicroAreasChange,
  onManagementTypeChange,
  onMonitorTypeChange,
  onFrequencyChange,
  onDayToggle,
  onStartDateChange,
}: PastureManagementFormV2Props) {
  const { t, i18n } = useTranslation();
  const isRange = isType(value, "Em faixa");
  const isNoMonitoring = isType(value, "Sem monitoramento");
  const isReadOnly = disabled || historyMode;
  const scheduleLabel = getScheduleLabel(value, t);
  const showWeekDays =
    !isRange &&
    (value.frequency?.value === 1 ||
      value.frequency?.value === 2);

  function update(partial: Partial<PastureManagementFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  return (
    <form className="ov2-property-form ov2-batch-form">
      <FormCardV2>
        <div className="pasture-management-v2">
          <div className="pasture-management-v2__fields">
            {historyMode && (
              <div className="pasture-management-v2__history-dates">
                <div className="pasture-management-v2__date-tag">
                  <small>{t("formsV2.from")}</small>
                  <div>
                    <h6>
                      {startDate ? format(startDate, "dd/MM/yyyy") : null}
                    </h6>
                    <CalendarIcon />
                  </div>
                </div>
                <div className="pasture-management-v2__date-tag">
                  <small>{t("formsV2.to")}</small>
                  <div>
                    <h6>{endDate ? format(endDate, "dd/MM/yyyy") : null}</h6>
                    <CalendarIcon />
                  </div>
                </div>
              </div>
            )}

            <FormGridV2>
              <DropdownV2
                name="farm"
                label={t("formsV2.farm")}
                value={value.farm}
                options={farms}
                searchable
                disabled={isReadOnly}
                error={errors.farm}
                emptyMessage={t("formsV2.noFarms")}
                onChange={(farm) => {
                  onFarmChange && onFarmChange(farm);
                  update({ farm, area: null, microAreas: [] });
                }}
              />
              <DropdownV2
                name="area"
                label={t("formsV2.area")}
                value={value.area}
                options={areas}
                searchable
                disabled={isReadOnly || !value.farm}
                error={errors.area}
                emptyMessage={t("formsV2.noAreas")}
                onChange={(area) => {
                  onAreaChange && onAreaChange(area);
                  update({ area, microAreas: [] });
                }}
              />
              <MultiDropdownV2
                name="microAreas"
                label={t("formsV2.paddocks")}
                value={value.microAreas}
                options={microAreas}
                searchable
                disabled={isReadOnly || !value.area}
                error={errors.microAreas}
                emptyMessage={t("formsV2.noPaddocksAvailable")}
                onChange={(selected) => {
                  onMicroAreasChange && onMicroAreasChange(selected);
                  update({ microAreas: selected });
                }}
              />
              <DropdownV2
                name="type"
                label={t("formsV2.grazingType")}
                value={value.type}
                options={managementTypes}
                searchable
                disabled={isReadOnly}
                error={errors.type}
                emptyMessage={t("formsV2.noGrazingType")}
                onChange={(type) => {
                  const typeInfo = type?.info;
                  const rangeMonitor =
                    getOptionDomainName(type) === "Em faixa" &&
                    Array.isArray(typeInfo) &&
                    typeInfo[0]
                      ? {
                          label: typeInfo[0].name,
                          value: typeInfo[0].id,
                          info: typeInfo[0],
                          domainName: typeInfo[0].domainName || typeInfo[0].name,
                        } as OptionV2
                      : null;
                  onManagementTypeChange && onManagementTypeChange(type);
                  update({
                    type,
                    monitor: rangeMonitor,
                    frequency: null,
                    heightInput: 0,
                    heightOutput: 0,
                    occupationDays: 0,
                    restDays: 0,
                    maxRate: 10,
                    rateIn: 0,
                    rateOut: 0,
                    range: 0,
                    executionPerDay: 0,
                  });
                }}
              />
              {!isRange && !isNoMonitoring && monitorTypes.length > 0 && (
                <DropdownV2
                  name="monitor"
                  label={t("formsV2.monitoringType")}
                  value={value.monitor}
                  options={monitorTypes}
                  searchable
                  disabled={isReadOnly}
                  error={errors.monitor}
                  emptyMessage={t("formsV2.noMonitoringType")}
                  onChange={(monitor) => {
                    onMonitorTypeChange && onMonitorTypeChange(monitor);
                    const isRating = getOptionDomainName(monitor) === "Nota";
                    update({
                      monitor,
                      frequency:
                        getOptionDomainName(monitor) === "Dias" ? null : value.frequency,
                      maxRate: isRating ? 10 : value.maxRate,
                      rateIn: isRating ? 1 : value.rateIn,
                      rateOut: isRating ? 1 : value.rateOut,
                    });
                  }}
                />
              )}

              {isMonitor(value, "Altura") && (
                <>
                  <CounterInputV2
                    name="heightInput"
                    label={t("formsV2.entryHeight")}
                    value={value.heightInput}
                    max={999}
                    suffix="cm"
                    disabled={isReadOnly}
                    error={errors.heightInput}
                    onChange={(heightInput) =>
                      update({
                        heightInput,
                        heightOutput:
                          value.heightOutput > heightInput
                            ? heightInput
                            : value.heightOutput,
                      })
                    }
                  />
                  <CounterInputV2
                    name="heightOutput"
                    label={t("formsV2.exitHeight")}
                    value={value.heightOutput}
                    max={value.heightInput || 999}
                    suffix="cm"
                    disabled={isReadOnly}
                    error={errors.heightOutput}
                    onChange={(heightOutput) => update({ heightOutput })}
                  />
                </>
              )}

              {isMonitor(value, "Dias") && (
                <>
                  <CounterInputV2
                    name="occupationDays"
                    label={t("formsV2.occupationDays")}
                    value={value.occupationDays}
                    max={999}
                    suffix={t("formsV2.daysSuffix")}
                    disabled={isReadOnly}
                    error={errors.occupationDays}
                    onChange={(occupationDays) => update({ occupationDays })}
                  />
                  <CounterInputV2
                    name="restDays"
                    label={t("formsV2.restDays")}
                    value={value.restDays}
                    max={999}
                    suffix={t("formsV2.daysSuffix")}
                    disabled={isReadOnly}
                    error={errors.restDays}
                    onChange={(restDays) => update({ restDays })}
                  />
                </>
              )}

              {isMonitor(value, "Nota") && (
                <>
                  <DropdownV2
                    name="ratingRange"
                    label={t("formsV2.ratingScale")}
                    value={
                      ratingRangeOptions.find(
                        (option) => option.value === value.maxRate
                      ) || ratingRangeOptions[1]
                    }
                    options={ratingRangeOptions}
                    disabled={isReadOnly}
                    error={errors.maxRate}
                    emptyMessage={t("formsV2.noRatingScale")}
                    onChange={(ratingRange) =>
                      update(
                        normalizeRatingValues(
                          ratingRange?.value,
                          value.rateIn,
                          value.rateOut
                        )
                      )
                    }
                  />
                  <CounterInputV2
                    name="rateIn"
                    label={t("formsV2.entryRating")}
                    value={value.rateIn}
                    min={1}
                    max={normalizeMaxRate(value.maxRate)}
                    disabled={isReadOnly}
                    error={errors.rateIn}
                    onChange={(rateIn) =>
                      update({
                        rateIn,
                        rateOut: Math.min(value.rateOut, rateIn),
                      })
                    }
                  />
                  <CounterInputV2
                    name="rateOut"
                    label={t("formsV2.exitRating")}
                    value={value.rateOut}
                    min={1}
                    max={Math.min(
                      value.rateIn,
                      normalizeMaxRate(value.maxRate)
                    )}
                    disabled={isReadOnly}
                    error={errors.rateOut}
                    onChange={(rateOut) => update({ rateOut })}
                  />
                </>
              )}

              {isRange && (
                <>
                  <CounterInputV2
                    name="range"
                    label={t("formsV2.stripQuantity")}
                    value={value.range}
                    max={999}
                    disabled={isReadOnly}
                    error={errors.range}
                    onChange={(range) => update({ range })}
                  />
                  <CounterInputV2
                    name="executionPerDay"
                    label={t("formsV2.executionsPerDay")}
                    value={value.executionPerDay}
                    max={999}
                    disabled={isReadOnly}
                    error={errors.executionPerDay}
                    onChange={(executionPerDay) => update({ executionPerDay })}
                  />
                </>
              )}

              {(isMonitor(value, "Altura") || isMonitor(value, "Nota")) && (
                <DropdownV2
                  name="frequency"
                  label={t("formsV2.monitoringFrequency")}
                  value={value.frequency}
                  options={frequencyOptions}
                  disabled={isReadOnly}
                  error={errors.frequency}
                  emptyMessage={t("formsV2.noFrequency")}
                  onChange={(frequency) => {
                    onFrequencyChange && onFrequencyChange(frequency);
                    update({ frequency });
                  }}
                />
              )}
            </FormGridV2>

            {!historyMode && scheduleLabel && (
              <span className="pasture-management-v2__schedule-label">
                {scheduleLabel}
              </span>
            )}

            {showWeekDays && (
              <div className="pasture-management-v2__week-days">
                {weekDays.map((day, index) => (
                  <button
                    className={`pasture-management-v2__week-day ${
                      selectedDays[index]
                        ? "pasture-management-v2__week-day--selected"
                        : ""
                    }`}
                    key={day}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => onDayToggle && onDayToggle(index)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            )}

            {!historyMode && needsSchedule(value) && (
              <div className="pasture-management-v2__day-picker">
                <DayPicker
                  mode="single"
                  selected={startDate}
                  fromDate={new Date()}
                  disabled={{ dayOfWeek: disabledDays }}
                  onDayClick={(day) =>
                    onStartDateChange && onStartDateChange(day)
                  }
                  showOutsideDays
                  fixedWeeks
                  locale={i18n.language.startsWith("es") ? es : ptBR}
                />
                {errors.startDate && (
                  <small className="ov2-field__error animation__fadeIn">
                    {errors.startDate}
                  </small>
                )}
              </div>
            )}
          </div>

          {mapSlot && (
            <div className="pasture-management-v2__map">{mapSlot}</div>
          )}
        </div>
      </FormCardV2>
    </form>
  );
}
